import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, Plus, Trash2, Move, Users, UserCheck, X } from 'lucide-react';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SIGNER_TYPES = [
  { value: 'supervisor', label: 'Supervisor Sig', color: 'bg-blue-500', lightColor: 'bg-blue-100 border-blue-400', textColor: 'text-blue-700' },
  { value: 'employee', label: 'Employee Sig', color: 'bg-green-500', lightColor: 'bg-green-100 border-green-400', textColor: 'text-green-700' },
  { value: 'both', label: 'Both Sig', color: 'bg-purple-500', lightColor: 'bg-purple-100 border-purple-400', textColor: 'text-purple-700' }
];

const FIELD_TYPES = [
  { value: 'signature_supervisor', label: 'Supervisor Signature', color: 'bg-blue-500', lightColor: 'bg-blue-100 border-blue-400', textColor: 'text-blue-700', category: 'signature' },
  { value: 'signature_employee', label: 'Employee Signature', color: 'bg-green-500', lightColor: 'bg-green-100 border-green-400', textColor: 'text-green-700', category: 'signature' },
  { value: 'signature_external', label: 'External / Subbie Signature', color: 'bg-rose-500', lightColor: 'bg-rose-100 border-rose-400', textColor: 'text-rose-700', category: 'signature' },
  { value: 'signature_both', label: 'Both Signatures', color: 'bg-purple-500', lightColor: 'bg-purple-100 border-purple-400', textColor: 'text-purple-700', category: 'signature' },
  { value: 'text_supervisor_fill', label: 'Text — Supervisor Fills', color: 'bg-amber-500', lightColor: 'bg-amber-100 border-amber-400', textColor: 'text-amber-700', category: 'fill' },
  { value: 'date_supervisor_fill', label: 'Date — Supervisor Fills', color: 'bg-teal-500', lightColor: 'bg-teal-100 border-teal-400', textColor: 'text-teal-700', category: 'fill' },
  { value: 'external_name', label: 'External Name', color: 'bg-pink-500', lightColor: 'bg-pink-100 border-pink-400', textColor: 'text-pink-700', category: 'fill' },
  { value: 'external_company', label: 'External Company', color: 'bg-fuchsia-500', lightColor: 'bg-fuchsia-100 border-fuchsia-400', textColor: 'text-fuchsia-700', category: 'fill' },
  { value: 'job_title', label: 'Job Title (auto)', color: 'bg-slate-500', lightColor: 'bg-slate-100 border-slate-400', textColor: 'text-slate-700', category: 'info' },
  { value: 'ticket_number', label: 'Ticket/Licence No.', color: 'bg-amber-500', lightColor: 'bg-amber-100 border-amber-400', textColor: 'text-amber-700', category: 'info' },
  { value: 'date_field', label: 'Date (auto)', color: 'bg-teal-500', lightColor: 'bg-teal-100 border-teal-400', textColor: 'text-teal-700', category: 'info' },
  { value: 'notes', label: 'Notes / Comments', color: 'bg-orange-500', lightColor: 'bg-orange-100 border-orange-400', textColor: 'text-orange-700', category: 'info' },
  { value: 'printed_name', label: 'Printed Name', color: 'bg-cyan-500', lightColor: 'bg-cyan-100 border-cyan-400', textColor: 'text-cyan-700', category: 'info' },
  { value: 'company_name', label: 'Company Name', color: 'bg-indigo-500', lightColor: 'bg-indigo-100 border-indigo-400', textColor: 'text-indigo-700', category: 'info' },
];

const PDFSignatureFieldPlacer = ({ fileUrl, signatureFields, onFieldsChange, users = [], jobAssignedUsers = [] }) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageWidth, setPageWidth] = useState(600);
  const [isPlacing, setIsPlacing] = useState(false);
  const [placingType, setPlacingType] = useState('supervisor');
  const [selectedField, setSelectedField] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const pageRef = useRef(null);

  // Responsive width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth - 32;
        setPageWidth(Math.min(w, 800));
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
  }, []);

  const getFieldsForPage = (page) => {
    return (signatureFields || []).filter(f => f.page === page);
  };

  const handlePageClick = (e) => {
    if (!isPlacing || dragging) return;
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Default signature field size (percentage of page)
    const fieldWidth = 25;
    const fieldHeight = 6;

    const newField = {
      field_id: `sig_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      page: currentPage,
      x: Math.max(0, Math.min(x - fieldWidth / 2, 100 - fieldWidth)),
      y: Math.max(0, Math.min(y - fieldHeight / 2, 100 - fieldHeight)),
      width: fieldWidth,
      height: placingType.startsWith('signature') ? fieldHeight : 4,
      label: FIELD_TYPES.find(t => t.value === placingType)?.label || placingType,
      field_type: placingType,
      signer_type: placingType === 'signature_supervisor' ? 'supervisor'
        : placingType === 'signature_employee' ? 'employee'
        : placingType === 'signature_both' ? 'both'
        : placingType === 'signature_external' || placingType === 'external_name' || placingType === 'external_company' ? 'external'
        : placingType === 'text_supervisor_fill' || placingType === 'date_supervisor_fill' ? 'supervisor_fill'
        : 'info',
      signer_ids: [],
      signer_roles: [],
      is_both: placingType === 'signature_both',
      auto_fill: placingType === 'ticket_number' ? 'ticket_numbers' : placingType === 'date_field' ? 'current_date' : null
    };

    onFieldsChange([...(signatureFields || []), newField]);
    setIsPlacing(false);
    setSelectedField(newField.field_id);
  };

  const handleFieldMouseDown = (e, field) => {
    e.stopPropagation();
    if (isPlacing) return;
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDragging(field.field_id);
    setDragOffset({
      x: e.clientX - (rect.left + (field.x / 100) * rect.width),
      y: e.clientY - (rect.top + (field.y / 100) * rect.height)
    });
    setSelectedField(field.field_id);
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !pageRef.current) return;
    const rect = pageRef.current.getBoundingClientRect();
    const field = signatureFields.find(f => f.field_id === dragging);
    if (!field) return;

    const x = ((e.clientX - rect.left - dragOffset.x) / rect.width) * 100;
    const y = ((e.clientY - rect.top - dragOffset.y) / rect.height) * 100;

    const updated = signatureFields.map(f => 
      f.field_id === dragging 
        ? { ...f, x: Math.max(0, Math.min(x, 100 - f.width)), y: Math.max(0, Math.min(y, 100 - f.height)) }
        : f
    );
    onFieldsChange(updated);
  }, [dragging, dragOffset, signatureFields, onFieldsChange]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  const removeField = (fieldId) => {
    onFieldsChange(signatureFields.filter(f => f.field_id !== fieldId));
    if (selectedField === fieldId) setSelectedField(null);
  };

  const updateFieldLabel = (fieldId, label) => {
    onFieldsChange(signatureFields.map(f => f.field_id === fieldId ? { ...f, label } : f));
  };

  const updateFieldSignerType = (fieldId, signer_type) => {
    onFieldsChange(signatureFields.map(f => f.field_id === fieldId ? { ...f, signer_type, is_both: signer_type === 'both' } : f));
  };

  const getSignerTypeInfo = (field) => {
    const ft = FIELD_TYPES.find(t => t.value === field.field_type);
    if (ft) return ft;
    if (field.is_both) return SIGNER_TYPES[2];
    return SIGNER_TYPES.find(s => s.value === field.signer_type) || FIELD_TYPES[1];
  };

  const currentPageFields = getFieldsForPage(currentPage);
  const totalFields = (signatureFields || []).length;

  return (
    <div ref={containerRef} className="space-y-3" data-testid="pdf-signature-placer">
      {/* Toolbar */}
      <div className="p-3 bg-slate-100 rounded-lg border-2 border-slate-200 space-y-2" data-testid="field-placer-toolbar">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase text-slate-500 mr-1">Signatures:</span>
          {FIELD_TYPES.filter(t => t.category === 'signature').map(type => (
            <button
              key={type.value}
              type="button"
              onClick={() => { setPlacingType(type.value); setIsPlacing(true); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border-2 ${
                isPlacing && placingType === type.value
                  ? `${type.lightColor} ring-2 ring-offset-1`
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`place-${type.value}`}
            >
              {type.value.includes('supervisor') ? <UserCheck className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
              {type.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase text-slate-500 mr-1">Fill-in Fields:</span>
          {FIELD_TYPES.filter(t => t.category === 'fill').map(type => (
            <button
              key={type.value}
              type="button"
              onClick={() => { setPlacingType(type.value); setIsPlacing(true); }}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all border-2 ${
                isPlacing && placingType === type.value
                  ? `${type.lightColor} ring-2 ring-offset-1`
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`place-${type.value}`}
            >
              {type.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase text-slate-500 mr-1">Info Fields:</span>
          {FIELD_TYPES.filter(t => t.category === 'info').map(type => (
            <button
              key={type.value}
              type="button"
              onClick={() => { setPlacingType(type.value); setIsPlacing(true); }}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all border-2 ${
                isPlacing && placingType === type.value
                  ? `${type.lightColor} ring-2 ring-offset-1`
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`place-${type.value}`}
            >
              {type.label}
            </button>
          ))}
          {isPlacing && (
            <button type="button" onClick={() => setIsPlacing(false)} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-lg border border-red-300">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">{totalFields} field(s) placed</span>
        </div>
      </div>

      {isPlacing && (
        <div className="p-2 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800 text-center font-medium">
          Click on the PDF where you want to place the signature field
        </div>
      )}

      {/* PDF Viewer */}
      <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-slate-200">
        {/* Page navigation */}
        <div className="flex items-center justify-between p-2 bg-slate-700 text-white">
          <button
            type="button"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="p-1.5 rounded hover:bg-slate-600 disabled:opacity-30"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium">
            Page {currentPage} of {numPages || '...'} 
            {currentPageFields.length > 0 && ` (${currentPageFields.length} signature field${currentPageFields.length > 1 ? 's' : ''})`}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage(p => Math.min(numPages || 1, p + 1))}
            disabled={currentPage >= (numPages || 1)}
            className="p-1.5 rounded hover:bg-slate-600 disabled:opacity-30"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* PDF Page with overlay */}
        <div 
          className="relative mx-auto"
          style={{ width: pageWidth, cursor: isPlacing ? 'crosshair' : 'default' }}
          onClick={handlePageClick}
        >
          <div ref={pageRef} className="relative">
            <Document file={fileUrl} onLoadSuccess={onDocumentLoadSuccess} loading={<div className="p-12 text-center text-slate-500">Loading PDF...</div>}>
              <Page pageNumber={currentPage} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
            </Document>

            {/* Signature field overlays */}
            {currentPageFields.map(field => {
              const typeInfo = getSignerTypeInfo(field);
              const isSelected = selectedField === field.field_id;
              return (
                <div
                  key={field.field_id}
                  className={`absolute border-2 border-dashed rounded cursor-move transition-shadow ${typeInfo.lightColor} ${isSelected ? 'ring-2 ring-orange-500 shadow-lg' : 'hover:shadow-md'}`}
                  style={{
                    left: `${field.x}%`,
                    top: `${field.y}%`,
                    width: `${field.width}%`,
                    height: `${field.height}%`,
                    zIndex: isSelected ? 20 : 10,
                    opacity: 0.85
                  }}
                  onMouseDown={(e) => handleFieldMouseDown(e, field)}
                  onClick={(e) => { e.stopPropagation(); setSelectedField(field.field_id); }}
                  data-testid={`sig-field-${field.field_id}`}
                >
                  <div className="flex items-center justify-between h-full px-1.5 overflow-hidden">
                    <span className={`text-[10px] font-bold uppercase truncate ${typeInfo.textColor}`}>
                      {field.label}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeField(field.field_id); }}
                      className="flex-shrink-0 p-0.5 rounded hover:bg-red-200 text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Field List / Editor */}
      {totalFields > 0 && (
        <div className="space-y-2">
          <h5 className="text-xs font-bold uppercase text-slate-500">Signature Fields ({totalFields})</h5>
          {(signatureFields || []).map((field, idx) => {
            const typeInfo = getSignerTypeInfo(field);
            const isSelected = selectedField === field.field_id;
            return (
              <div 
                key={field.field_id} 
                className={`flex items-center gap-3 p-2.5 rounded-lg border-2 transition-colors cursor-pointer ${isSelected ? 'border-orange-400 bg-orange-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                onClick={() => { setSelectedField(field.field_id); setCurrentPage(field.page); }}
              >
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${typeInfo.color}`} />
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={field.label}
                    onChange={(e) => updateFieldLabel(field.field_id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full text-sm font-medium bg-transparent border-none outline-none"
                    placeholder="Label..."
                  />
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-slate-400">Page {field.page}</span>
                    <select
                      value={field.field_type || (field.is_both ? 'signature_both' : field.signer_type === 'supervisor' ? 'signature_supervisor' : 'signature_employee')}
                      onChange={(e) => {
                        const ft = FIELD_TYPES.find(t => t.value === e.target.value);
                        const updated = signatureFields.map(f => f.field_id === field.field_id ? {
                          ...f,
                          field_type: e.target.value,
                          signer_type: e.target.value === 'signature_supervisor' ? 'supervisor' : e.target.value === 'signature_employee' ? 'employee' : e.target.value === 'signature_both' ? 'both' : 'info',
                          is_both: e.target.value === 'signature_both',
                          label: ft?.label || e.target.value
                        } : f);
                        onFieldsChange(updated);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] border rounded px-1 py-0.5 bg-white"
                    >
                      <optgroup label="Signatures">
                        {FIELD_TYPES.filter(t => t.category === 'signature').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Info Fields">
                        {FIELD_TYPES.filter(t => t.category === 'info').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeField(field.field_id); }}
                  className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PDFSignatureFieldPlacer;
